import React, { useEffect } from 'react'
import { Image } from '@christies/react/components/Editorial'
import { withParsedContext, ParsedAnchorValue, ImageValue, SelectValue } from '../../withJSSContext'
import { Theme } from '@christies/react/foundations'
import { TrackingEvents, track, debounce } from '@christies/react/utils'
import { RenderFields, getAspectRatio } from '../../utils'
import { QUICK_VIEW_ROOT_ID } from '../../../../helpers/modals'
import { TFunction } from 'i18next'
import { StoredImageValue, useImageStore } from '../../imagesStoreContext'

interface Props {
  pageEditing: boolean
  parsedFields: {
    'Base Image': ImageValue
    'Lightbox Image': ImageValue
    'Image Description': string
    'Image Type': SelectValue<'6' | '8' | '12'>
    'Image Caption': string
    'Image Link': ParsedAnchorValue
    'Show on Lightbox': boolean
    'Screen Reader Caption': string
    instanceCount: number | string
    imageCount: number
  }
  parsedRouteFields: {
    'Story Theme': SelectValue<Theme | undefined>
  }
  t: TFunction
}

const MagazinesSingleImageModule: React.FC<Props> = ({ parsedFields, parsedRouteFields, t }) => {
  const { setStoreImage, sharedStoreImages } = useImageStore()

  const handleZoomChange = debounce((value: number): void => {
    track({
      event: TrackingEvents.INTERACTION,
      data: {
        interaction: {
          type: `enlarge image:${value}`,
          location: t('feature-discovery-magazines-singleimage-analytics-location', {
            defaultValue: 'stories:single image:{{value}}',
            value: parsedFields?.instanceCount,
          }),
          destination: 'quick view',
        },
      },
    })
  }, 300)

  const currentImageInstance = parsedFields?.imageCount
  const description = parsedFields?.['Image Description']

  const lightbox = {
    ...getAspectRatio({
      ...parsedFields?.['Lightbox Image'],
      alt: parsedFields?.['Screen Reader Caption'] || parsedFields?.['Base Image']?.alt,
    }),
    id: `singleImage-${currentImageInstance}`,
    description,
  } as StoredImageValue
  const baseImage = {
    ...getAspectRatio({
      ...parsedFields?.['Base Image'],
      alt: parsedFields?.['Screen Reader Caption'] || parsedFields?.['Base Image']?.alt,
    }),
    id: `singleImage-${currentImageInstance}`,
    description,
  } as StoredImageValue

  useEffect(() => {
    setStoreImage && setStoreImage(currentImageInstance, (lightbox?.src && lightbox) || baseImage)
  }, [])

  const currentSlide = sharedStoreImages?.filter((item) => item?.id === lightbox?.id || item.id === baseImage.id)[0]
    ?.currentSlide

  const imageProps = {
    image: parsedFields?.['Base Image'] ? baseImage : lightbox,
    description,
    lgWidth: parsedFields?.['Image Type']?.key,
    cta: {
      text: parsedFields?.['Image Caption'],
      href: parsedFields?.['Image Link']?.href,
      target: parsedFields?.['Image Link']?.target,
    },
    quickViewProps: {
      quickViewRootId: QUICK_VIEW_ROOT_ID,
      title: t('feature-discovery-magazines-quickview-title'),
      ariaLabel: t('feature-discovery-magazines-quickview-arialabel'),
      closeButton: t('feature-discovery-magazines-quickview-closebutton'),
      previousButtonAriaLabel: t('feature-discovery-magazines-quickview-previousbuttonarialabel'),
      nextButtonAriaLabel: t('feature-discovery-magazines-quickview-nextbuttonarialabel'),
      slideDividerLabel: t('feature-discovery-magazines-quickview-sliderpagenumberparticle'),
    },
    quickViewZoomProps: {
      rangeSliderAriaLabel: t('feature-discovery-magazines-quickview-rangesliderarialabel'),
      incrementAriaLabel: t('feature-discovery-magazines-quickview-incrementarialabel'),
      decrementAriaLabel: t('feature-discovery-magazines-quickview-decrementarialabel'),
      onChange: handleZoomChange,
    },
    theme: parsedRouteFields?.['Story Theme']?.key,
    enableGallery: parsedFields?.['Show on Lightbox'],
    onClick: () =>
      track({
        event: TrackingEvents.INTERACTION,
        data: {
          interaction: {
            type: `single image ${parsedFields?.instanceCount}: caption 1`,
            location: t('feature-discovery-magazines-quickview-analytics-opened-location'),
            destination: 'lot',
          },
        },
      }),
    currentSlide: currentSlide,
    galleryImages: sharedStoreImages,
    t: {
      galleryAccessibilityLabel: t('foundation-discovery-accessibility-images-labels-open-image-in-gallery'),
      linkAccessibilityLabel: t('foundation-discovery-accessibility-images-labels-open-link'),
    },
  }

  return (
    <RenderFields parsedFields={parsedFields}>
      <Image {...imageProps} />
    </RenderFields>
  )
}

export default withParsedContext(MagazinesSingleImageModule as React.FC)
